import {
  Box,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Select,
  Text,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  ROR_CALCULATION_VARIANTS,
  ROR_DANGER_MAPS,
  ROR_RISK_MAPS,
  RorCalculationVariant,
  RorConfig,
} from "@nens/ldo/api-v1";

interface ExportRorFieldsProps {
  readonly: boolean;
  value: RorConfig;
  onChange: (e: Partial<RorConfig>) => void;
  formErrorKeys: string[];
}

export function ExportRorFields({
  readonly,
  value,
  onChange,
  formErrorKeys,
}: ExportRorFieldsProps) {
  const intl = useIntl();

  const DANGER_MAPS: Record<string, string> = {
    vg1: intl.formatMessage({ id: "danger_map.vg1", defaultMessage: "Inundable area" }),
    vg2: intl.formatMessage({ id: "danger_map.vg2", defaultMessage: "Maximum water depth" }),
    vg3: intl.formatMessage({ id: "danger_map.vg3", defaultMessage: "Maximum water velocity" }),
    gg1: intl.formatMessage({ id: "danger_map.gg1", defaultMessage: "Arrival time" }),
    gg2: intl.formatMessage({ id: "danger_map.gg2", defaultMessage: "Inundation period" }),
    gg3: intl.formatMessage({ id: "danger_map.gg3", defaultMessage: "Rise period" }),
    gg4: intl.formatMessage({
      id: "danger_map.gg4",
      defaultMessage: "Simultaneously threatened areas",
    }),
    gg5: intl.formatMessage({ id: "danger_map.gg5", defaultMessage: "Sources of inundation" }),
  };

  const RISK_MAPS: Record<string, string> = {
    vr1: intl.formatMessage({
      id: "risk_map.vr1",
      defaultMessage: "Indication of potential victims",
    }),
    vr2: intl.formatMessage({
      id: "risk_map.vr2",
      defaultMessage: "Economic activity of potentially affected area",
    }),
    vr3: intl.formatMessage({
      id: "risk_map.vr3",
      defaultMessage: "Installations as meant in appendix I to Directive 2008/1/EG",
    }),
    vr4: intl.formatMessage({
      id: "risk_map.vr4",
      defaultMessage: "Areas appointed protected in directive 2000/60/EG",
    }),
    gr1: intl.formatMessage({ id: "risk_map.gr1", defaultMessage: "Damage potential" }),
    gr2: intl.formatMessage({
      id: "risk_map.gr2",
      defaultMessage: "Especially vulnerable objects",
    }),
    gr3: intl.formatMessage({
      id: "risk_map.gr3",
      defaultMessage: "Especially vulnerable cultural history objects",
    }),
  };

  return (
    <Flex
      flexDirection={{
        base: "column",
        md: "row",
      }}
    >
      <Box my="4" border="1px solid #E2E8F0" rounded="xl" p="2">
        <FormControl my="4">
          <FormLabel fontWeight="semibold">
            <FormattedMessage
              id="pages.export_scenarios.new.cell_size"
              defaultMessage="Raster cell size"
            />
          </FormLabel>
          <NumberInput
            defaultValue={value.cell_size}
            min={5}
            max={100000}
            precision={0}
            keepWithinRange={true}
            clampValueOnBlur={true}
            isReadOnly={readonly}
            onChange={(asString, asNumber) => {
              if (!isNaN(asNumber) && asNumber !== value.cell_size && asNumber >= 5) {
                onChange({ cell_size: asNumber });
              }
            }}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
        <FormControl my="4">
          <FormLabel fontWeight="semibold">
            <FormattedMessage
              id="pages.export_scenarios.new.calculation_variant"
              defaultMessage="Calculation variant"
            />
          </FormLabel>
          <Select
            value={value.calculation_variant}
            isReadOnly={readonly}
            onChange={(e) =>
              onChange({ calculation_variant: e.target.value as RorCalculationVariant })
            }
          >
            {ROR_CALCULATION_VARIANTS.map((variant) => (
              <option key={variant} value={variant}>
                {variant}
              </option>
            ))}
          </Select>
        </FormControl>
      </Box>
      <FormControl my="4" border="1px solid #E2E8F0" rounded="xl" p="2">
        <FormLabel fontWeight="semibold">
          <FormattedMessage
            id="pages.export_scenarios.new.danger_maps"
            defaultMessage="Danger maps"
          />
        </FormLabel>
        <CheckboxGroup
          value={value.danger_maps}
          onChange={(e) => readonly || onChange({ danger_maps: e as RorConfig["danger_maps"] })}
        >
          <Flex flexDirection="column">
            {ROR_DANGER_MAPS.map((dangerMap) => (
              <Checkbox
                key={dangerMap}
                value={dangerMap}
                isReadOnly={readonly || dangerMap === "vg1"}
                isDisabled={dangerMap === "vg1"}
              >
                {dangerMap.toUpperCase()} {DANGER_MAPS[dangerMap] ?? ""}
              </Checkbox>
            ))}
          </Flex>
        </CheckboxGroup>
        {formErrorKeys.includes("maps") ? (
          <Text color="red.500">
            <FormattedMessage
              id="pages.export_scenarios.new.maps_error"
              defaultMessage="Please select at least one map"
            />
          </Text>
        ) : null}
      </FormControl>
      <FormControl my="4" border="1px solid #E2E8F0" rounded="xl" p="2">
        <FormLabel fontWeight="semibold">
          <FormattedMessage id="pages.export_scenarios.new.risk_maps" defaultMessage="Risk maps" />
        </FormLabel>
        <CheckboxGroup
          value={value.risk_maps}
          onChange={(e) => readonly || onChange({ risk_maps: e as RorConfig["risk_maps"] })}
        >
          <Flex flexDirection="column">
            {ROR_RISK_MAPS.map((riskMap) => (
              <Checkbox key={riskMap} value={riskMap} isReadOnly={readonly}>
                {riskMap.toUpperCase()} {RISK_MAPS[riskMap] ?? ""}
              </Checkbox>
            ))}
          </Flex>
        </CheckboxGroup>
      </FormControl>
    </Flex>
  );
}
